import type { PortfolioRow } from './portfolioRows';

export type HierarchyLevel = 'level1' | 'level2' | 'level3';

export const HIERARCHY_LEVELS: ReadonlyArray<HierarchyLevel> = ['level1', 'level2', 'level3'];

export type OwnerLevels = Partial<Record<HierarchyLevel, string | null>>;

export type OwnerHierarchy = ReadonlyMap<string, OwnerLevels>;

export type ScopeSection = HierarchyLevel | 'owner';

export interface ScopeOption {
  id: string;
  section: ScopeSection;
  label: string;
  ownerIds: string[];
}

export interface PortfolioOwner {
  id: string;
  name: string;
}

const OWNER_PREFIX = 'owner';

export const ownerScopeId = (ownerId: string): string => `${OWNER_PREFIX}:${ownerId}`;

export const levelScopeId = (level: HierarchyLevel, value: string): string => `${level}:${value}`;

const byLabel = (a: { label: string }, b: { label: string }): number => a.label.localeCompare(b.label, 'pt-BR');

export const levelValueOf = (hierarchy: OwnerHierarchy, ownerId: string | null, level: HierarchyLevel): string | null => {
  if (!ownerId) return null;
  const value = hierarchy.get(ownerId)?.[level]?.trim();
  return value || null;
};

export const portfolioOwners = (rows: ReadonlyArray<PortfolioRow>): PortfolioOwner[] => {
  const byId = new Map<string, string>();
  rows.forEach((row) => {
    if (row.ownerId && row.ownerName && !byId.has(row.ownerId)) byId.set(row.ownerId, row.ownerName);
  });
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
};

export const availableLevels = (hierarchy: OwnerHierarchy, owners: ReadonlyArray<PortfolioOwner>): HierarchyLevel[] => (
  HIERARCHY_LEVELS.filter((level) => owners.some((owner) => levelValueOf(hierarchy, owner.id, level) !== null))
);

export const buildScopeOptions = (owners: ReadonlyArray<PortfolioOwner>, hierarchy: OwnerHierarchy): ScopeOption[] => {
  const levelOptions = availableLevels(hierarchy, owners).flatMap((level) => {
    const members = new Map<string, string[]>();
    owners.forEach((owner) => {
      const value = levelValueOf(hierarchy, owner.id, level);
      if (value) members.set(value, [...(members.get(value) ?? []), owner.id]);
    });
    return [...members.entries()]
      .map(([value, ownerIds]) => ({ id: levelScopeId(level, value), section: level, label: value, ownerIds }))
      .sort(byLabel);
  });
  const ownerOptions = owners.map((owner) => ({
    id: ownerScopeId(owner.id), section: 'owner' as const, label: owner.name, ownerIds: [owner.id],
  }));
  return [...levelOptions, ...ownerOptions];
};

export const resolveScopeOwnerIds = (
  selectedIds: ReadonlyArray<string>,
  options: ReadonlyArray<ScopeOption>,
): ReadonlySet<string> | null => {
  if (selectedIds.length === 0) return null;
  const byId = new Map(options.map((option) => [option.id, option]));
  const ownerIds = new Set<string>();
  selectedIds.forEach((id) => {
    const option = byId.get(id);
    if (option) option.ownerIds.forEach((ownerId) => ownerIds.add(ownerId));
    else if (id.startsWith(`${OWNER_PREFIX}:`)) ownerIds.add(id.slice(OWNER_PREFIX.length + 1));
  });
  return ownerIds;
};

export const isInScope = (row: PortfolioRow, ownerIds: ReadonlySet<string> | null): boolean => (
  ownerIds === null || (row.ownerId !== null && ownerIds.has(row.ownerId))
);
